import React from 'react';
import firebase from 'firebase/app';
import { log, useCurrentAgreement, useCurrentUser } from '@economic/react-app';
import { FirestoreUserPreferences } from '../../../types';

export default function useFirestore(): [
    firebase.firestore.DocumentReference | undefined,
    firebase.firestore.DocumentReference | undefined,
    firebase.firestore.Firestore | undefined,
] {
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();

    if (
        !process.env.REACT_APP_FIRESTORE_API_KEY ||
        !process.env.REACT_APP_FIRESTORE_API_KEY ||
        !process.env.REACT_APP_FIRESTORE_PROJECT_ID
    ) {
        return [undefined, undefined, undefined];
    }

    const db = firebase.firestore();
    const agreementDoc = db.collection('agreements').doc(String(agreement.number));
    const userDoc = agreementDoc.collection('users').doc(user.guid);

    return [agreementDoc, userDoc, db];
}

export const useFirestoreUserPreferences = (): [
    FirestoreUserPreferences | undefined,
    (changeset: Partial<FirestoreUserPreferences>) => void,
    boolean,
] => {
    const [, user] = useFirestore();
    const [loading, setLoading] = React.useState(true);
    const [preferences, setPreferences] = React.useState<FirestoreUserPreferences | undefined>(undefined);

    const fetch = async () => {
        try {
            if (user) {
                const prefs: any = await (await user.get()).data();
                setPreferences({
                    ...prefs,
                    lastSeenNotificationsTimestamp: prefs.lastSeenNotificationsTimestamp?.toDate(),
                });
            }
        } catch {
            // oh well
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetch();
    }, []);

    const handleSetPreferences = async (changeset: Partial<FirestoreUserPreferences>) => {
        try {
            await user?.set(changeset, { merge: true });
        } catch {
            // oh well
        } finally {
            setPreferences(prefs => ({ ...prefs, ...changeset }));
        }
    };

    return [preferences, handleSetPreferences, loading];
};

/**
 * Provides a way to store agreement-level data to (current agreement) Firestore.
 * @param key The key that will appear on agreement document
 * @param defaultValue The default value to use, if does not exist on Firestore.
 * @returns An object containing the following: data, set function, clear function, and the loading flag
 */
export function useFirestoreAgreementData<T>(
    key: string,
    defaultValue: T
): { data: T; set: (data: T) => Promise<void>; clear: () => Promise<void>; loading: boolean } {
    const [agreement] = useFirestore();
    return firestoreKeyValueData(key, defaultValue, agreement);
}

/**
 * Provides a way to store user-level data to (current user) Firestore.
 * @param key The key that will appear on user document
 * @param defaultValue The default value to use, if does not exist on Firestore.
 * @param skip Indicator to skip the actual query call
 * @returns An object containing the following: data, set function, clear function, and the loading flag
 */
export function useFirestoreUserData<T>(
    key: string,
    defaultValue: T,
    skip?: boolean
): { data: T; set: (data: T) => Promise<void>; clear: () => Promise<void>; loading: boolean; error: any } {
    const [_, user] = useFirestore();
    return firestoreKeyValueData(key, defaultValue, user, skip);
}

function firestoreKeyValueData<T>(
    key: string,
    defaultValue: T,
    document: firebase.firestore.DocumentReference | undefined,
    skip?: boolean
): { data: T; set: (data: T) => Promise<void>; clear: () => Promise<void>; loading: boolean; error: any } {
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState();
    const [data, setData] = React.useState<T>(defaultValue);
    const [currentToken, setCurrentToken] = React.useState('');

    React.useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged(async user => {
            const userToken = await user?.getIdToken();
            if (userToken) {
                setCurrentToken(userToken);
            }
        });

        return () => unsubscribe();
    }, []);

    React.useEffect(() => {
        const fetch = async () => {
            try {
                if (document) {
                    const data: any = (await document.get()).data();
                    setData((data?.[key] as T) ?? defaultValue);
                }
            } catch (err) {
                setError(err);
                log.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (skip !== true) {
            fetch();
        } else {
            setLoading(false);
        }
    }, [currentToken]);

    const handleSetData = async (newData: T) => {
        try {
            const toUpdate = {};
            toUpdate[key] = newData;
            await document?.set(toUpdate, { merge: true });
        } catch (err) {
            setError(err);
            log.error(err);
        } finally {
            setData(newData);
        }
    };

    const handleClearData = async () => {
        try {
            const toDelete = {};
            toDelete[key] = firebase.firestore.FieldValue.delete();
            await document?.update(toDelete);
        } catch (err) {
            setError(err);
            log.error(err);
        } finally {
            setData(defaultValue);
        }
    };

    return { data, set: handleSetData, clear: handleClearData, loading, error };
}
